import { useState, useEffect } from 'react';
import { Box, Collapse, Alert, AlertTitle } from '@mui/material';
import { ErrorMessage } from '../../ErrorMessage';
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, AppState, AppStore } from '../../../store';
import { GenericSimpleForm } from '../../../components/Layouts/GenericSimpleForm';
import {
  MultiNumericFormFields,
  EAction,
  EActionType,
  EDataTableType,
} from '../../../data/Constants';
import { useTranslation } from 'react-i18next';
import { setAlertMessage } from '../../../store/states/AlertMessageSlice';
import { resetDialogMessage } from '../../../store/states/DialogSlice';
import { resetError } from '../../../store/states/ErrorSlice';
import {
  fetchMultiNumericFeatures,
  createMultiNumericFeature,
  updateMultiNumericFeature,
  fetchMultiNumericFeatureDetails,
} from '../../../store/states/FeatureSlice';

import type {
  IError,
  IManipulationProps,
  IMultiNumericFeatureData,
  IMultiNumericHandleCreateProps,
  IMultiNumericHandleResponseProps,
} from '../../../../types';
import type { IMultiNumericResponse } from '../../../../types/IApiTypes';

/**
 * This method call the create or update api to add or update the multi numeric feature to the db
 * @param {IMultiNumericCreateProps} props input props
 * @returns {void}
 */

async function callCreateOrUpdateApi(props: IMultiNumericHandleCreateProps): Promise<void> {
  const { manipulationType, formData, t, dispatch } = props;
  if (manipulationType === EActionType.Edit) {
    const updateResponse = await dispatch(
      updateMultiNumericFeature({
        request: { ...formData, name: formData.name.trimEnd(), code: formData.code.trim() },
      })
    );
    handleApiResponse({
      response: updateResponse.payload as IMultiNumericResponse,
      failMessage: t('messages.fail.update'),
      successMessage: t('messages.success.update'),
      dispatch,
    });
  } else {
    const createResponse = await dispatch(
      createMultiNumericFeature({
        request: { ...formData, name: formData.name.trimEnd(), code: formData.code.trim() },
      })
    );
    handleApiResponse({
      response: createResponse.payload as IMultiNumericResponse,
      failMessage: t('messages.fail.create'),
      successMessage: t('messages.success.create'),
      dispatch,
    });
  }
}

/**
 * This method check the create or update response and shows the success or error message
 * @param {IMultiNumericHandleApiResponseProps} props input params
 * @returns {void }
 */

function handleApiResponse(props: IMultiNumericHandleResponseProps) {
  const { response, failMessage, successMessage, dispatch } = props;

  //get the multiNumericFeature state
  const multiNumericFeature = AppStore.getState().feature.multiNumericFeature;

  //if response is valid then dispatch  the fetchMultiNumericFeatures api to fetch the updated list from the db
  if (response?.data) {
    dispatch(
      fetchMultiNumericFeatures({
        page: multiNumericFeature.currentPage,
        limit: multiNumericFeature.recordsPerPage,
        searchCode: multiNumericFeature.searchKey,
        caller: 'MultiNumericManipulationForm',
      })
    );
    dispatch(setAlertMessage({ show: true, message: successMessage, type: 'success' }));
    dispatch(resetDialogMessage());
  } else {
    const message = response?.error?.message ? response.error.message : failMessage;
    dispatch(setAlertMessage({ show: true, message: message, type: 'error' }));
  }
}

const loadFormData = async (
  props: IManipulationProps,
  setInitialMultiNumericData: (data: IMultiNumericFeatureData) => void,
  dispatch: AppDispatch
) => {
  const { id } = props;
  //dispatch the action with unique code to fetch its info
  const response = await dispatch(fetchMultiNumericFeatureDetails({ searchCode: id }));
  const payload = response?.payload as { data?: IMultiNumericFeatureData[] };
  if (response && !payload?.data) {
    return;
  }
  const multiNumericDetails: IMultiNumericFeatureData = payload.data![0];

  const others = MultiNumericFormFields.find((app) => app.field == 'others');
  if (multiNumericDetails.isActive) {
    others.values = others.values?.filter((item) => item.code != 'isActive');
  } else {
    // Check if "isActive" is already in the array
    const exists = others.values?.some((item) => item.code === 'isActive');

    if (!exists) {
      if (others.values) {
        others.values.push({ code: 'isActive', name: 'isActive' });
      }
    }
  }
  //set the multi numeric feature deatils to the local state to render the code and name in the dialog
  setInitialMultiNumericData(multiNumericDetails);
};

export const MultiNumericManipulationForm = (props: IManipulationProps) => {
  const manipulationType = props.type; //To get the type of manipulation being performed
  const { t } = useTranslation();
  const [displayAlertMessage, setDisplayAlertMessage] = useState<boolean>(false);
  const error = useSelector((state: AppState) => state.error);
  const dispatch = useDispatch<AppDispatch>();
  const initialData: IMultiNumericFeatureData = {
    name: '',
    code: '',
    isLocal: false,
    isActive: true,
  };
  const [initialFormData, setInitialFormData] = useState<IMultiNumericFeatureData>(initialData);

  const handleError = (error: IError, setDisplayAlertMessage: (show: boolean) => void) => {
    if (error.code && (error.action === EAction.Create || error.action === EAction.Update)) {
      setDisplayAlertMessage(true);
    }
  };

  useEffect(() => {
    if (manipulationType === EActionType.Edit) {
      loadFormData(props, setInitialFormData, dispatch);
    }
  }, []);

  useEffect(() => {
    handleError(error, setDisplayAlertMessage);
  }, [error]);

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
  };

  const onSubmit = (data: IMultiNumericFeatureData) => {
    dispatch(resetError());
    setDisplayAlertMessage(false);
    callCreateOrUpdateApi({ manipulationType, formData: data, t, dispatch });
  };

  return (
    <Box className="manipulationBox">
      <Box className="manipulationForm">
        <Collapse in={displayAlertMessage} className="show-alert">
          <Alert className="errorMessage" severity="error" onClose={handleClose}>
            <AlertTitle>
              <ErrorMessage error={error} manipulationType={manipulationType} />
            </AlertTitle>
          </Alert>
        </Collapse>
        <GenericSimpleForm
          formFields={MultiNumericFormFields}
          initialData={initialFormData}
          onSubmit={onSubmit}
          manipulationType={manipulationType}
          setDisplayAlertMessage={setDisplayAlertMessage}
          type={EDataTableType.MultiNumericFeature}
          uniqueKey="code"
        />
      </Box>
    </Box>
  );
};
